import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import {
  AlertCircle, ArrowDownUp, ChevronLeft, ChevronRight, Clock3, Folder, FolderOpen, HardDrive, X,
} from 'lucide-react';

import { loadReportFacets, loadReportFolders, loadReportInventory } from './api';
import { tx } from './i18n';
import { useDebounced } from './MobileAccounts';
import { FilterMenu, NUMBER, ago, counted, useNow } from './MobileParts';
import { Empty, Failed, Head, Loading, SearchField, ViewSwitch } from './parts';
import {
  DownloadReport, SourceMark, StatusPill, bytesLabel, dateLabel, durationLabel, openReportFile, reasonLabel,
  sourceLabel, statusLabel, text,
} from './ReportParts';
import { errorMessage } from './ui';
import { useLiveResource } from './useLive';

// Every report the platform has made, filed the way a file browser shows it: a
// folder per project, a folder per patient inside it, and that patient's report
// PDFs. The sidebar keeps the projects and two lists at hand (the last week, and
// what failed), and the search looks through every report. A link from the
// analytics page carries its filters in the address and opens as a flat list.
// A file opens through the report's own download route.

const inventory = (key, values) => tx(`ui.reports.inventory.${key}`, values);
const files = (key, values) => tx(`ui.reports.files.${key}`, values);
const WEEK = 7 * 86400000;
const PAGE = 100;
const VIEW_KEY = 'xo-reports-view';
// Filters an analytics link can carry. A project opens its folder instead.
const FILTERS = ['status', 'source', 'reason', 'reportType', 'language', 'pipeline', 'product', 'organization', 'company'];
const LISTS = {
  recent: { Icon: Clock3, query: () => ({ from: Date.now() - WEEK }) },
  failed: { Icon: AlertCircle, query: () => ({ status: 'failed' }) },
};
// Files are sorted by the server, folders here, by the same three choices.
const SORTS = {
  newest: { files: { sort: 'requested', direction: 'desc' }, folders: (a, b) => (b.latestAt ?? 0) - (a.latestAt ?? 0) },
  name: { files: { sort: 'type', direction: 'asc' }, folders: (a, b) => folderName(a).localeCompare(folderName(b)) },
  largest: { files: { sort: 'size', direction: 'desc' }, folders: (a, b) => b.bytes - a.bytes },
};

function folderName(folder) {
  if (folder.kind === 'project') {
    if (folder.id === 'none') return text('no_project');
    return folder.name || text('project_record', { id: folder.id });
  }
  return folder.name || inventory('patient_record', { id: folder.id });
}

function patientLabel(report) {
  if (report.patient === null) return inventory('no_patient');
  return report.patient.name || inventory('patient_record', { id: report.patient.id });
}

function storedView() {
  try { return window.localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid'; }
  catch (_error) { return 'grid'; }
}

// Below this width a report's details open over the page instead of beside the files.
const NARROW = '(max-width: 1100px)';

function useNarrow() {
  const [narrow, setNarrow] = useState(() => window.matchMedia(NARROW).matches);
  useEffect(() => {
    const query = window.matchMedia(NARROW);
    const update = () => setNarrow(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return narrow;
}

// The folder a report is filed in: its patient's, or loose among the reports without a project.
function folderOf(report) {
  if (report.patient === null) return { project: 'none' };
  return { project: report.project === null ? 'none' : report.project.id, patient: report.patient.id };
}

function Sidebar({ root, mode, list, project, onGo }) {
  const projects = root ? root.folders : [];
  const total = projects.reduce((sum, folder) => sum + folder.reports, 0);
  const bytes = projects.reduce((sum, folder) => sum + folder.bytes, 0);
  const active = (on) => `xo-lib-place${on ? ' is-active' : ''}`;
  return (
    <nav className="xo-lib-side" aria-label={text('places')}>
      <div className="xo-lib-places">
        <button type="button" className={active(mode === 'folder' && project === null)} onClick={() => onGo({})}>
          <HardDrive aria-hidden="true" /><span>{text('all_reports')}</span>
        </button>
        {Object.entries(LISTS).map(([code, { Icon }]) => (
          <button key={code} type="button" className={active(mode === 'list' && list === code)} onClick={() => onGo({ list: code })}>
            <Icon aria-hidden="true" /><span>{text(`list_${code}`)}</span>
          </button>
        ))}
      </div>
      {projects.length > 0 && (
        <div className="xo-lib-places">
          <h4>{text('projects')}</h4>
          {projects.map((folder) => (
            <button key={folder.id} type="button" title={folderName(folder)}
              className={active(mode === 'folder' && String(project) === String(folder.id))} onClick={() => onGo({ project: folder.id })}>
              <Folder aria-hidden="true" /><span>{folderName(folder)}</span><em>{NUMBER.format(folder.reports)}</em>
            </button>
          ))}
        </div>
      )}
      {root && (
        <p className="xo-lib-storage">
          <strong>{counted('ui.reports.inventory.count', total)}</strong><small>{bytesLabel(bytes)}</small>
        </p>
      )}
    </nav>
  );
}

// Where the library is open, as breadcrumbs: every level but the last can be clicked.
function Crumbs({ steps, onGo }) {
  return (
    <ol className="xo-lib-crumbs">
      {steps.map((step, index) => {
        const last = index === steps.length - 1;
        return (
          <li key={step.key}>
            {index > 0 && <ChevronRight aria-hidden="true" />}
            {last || !step.place ? <strong aria-current={last ? 'location' : undefined}>{step.label}</strong>
              : <button type="button" onClick={() => onGo(step.place)}>{step.label}</button>}
          </li>
        );
      })}
    </ol>
  );
}

function folderMeta(folder, now) {
  const count = counted('ui.reports.inventory.count', folder.reports);
  if (folder.kind === 'patient' && folder.subproject) return `${folder.subproject.code || folder.subproject.name} · ${count}`;
  return `${count} · ${ago(folder.latestAt, now)}`;
}

function FolderTile({ folder, now, onOpen }) {
  return (
    <li>
      <button type="button" className="xo-lib-tile is-folder" onClick={onOpen} title={folderName(folder)}>
        <span className="xo-lib-glyph"><Folder aria-hidden="true" /></span>
        <span className="xo-lib-name">
          <strong>{folderName(folder)}</strong>
          <small>{folderMeta(folder, now)}</small>
        </span>
        {folder.failed > 0 && <span className="xo-lib-flag">{text('failed_count', { count: NUMBER.format(folder.failed) })}</span>}
      </button>
    </li>
  );
}

// A page with a folded corner, drawn rather than rendered: the PDF itself is
// only opened on request, through its download route.
function Page({ status, large = false }) {
  return (
    <span className={`xo-lib-page is-${status}${large ? ' is-large' : ''}`} aria-hidden="true">
      <span className="xo-lib-sheet"><i /><i /><i /><i /><i /></span>
      <b>PDF</b>
    </span>
  );
}

function FileTile({ report, now, selected, withPatient, onSelect, onOpen }) {
  return (
    <li>
      <button type="button" className={`xo-lib-tile is-file${selected ? ' is-selected' : ''}`} aria-pressed={selected}
        onClick={onSelect} onDoubleClick={onOpen} title={report.file === null ? undefined : text('open_hint')}>
        <span className="xo-lib-preview"><Page status={report.status} /></span>
        <span className="xo-lib-name">
          <strong>{report.reportType.displayName}</strong>
          <small>{report.language.name} · {ago(report.requestedAt, now)}</small>
          {withPatient && <small>{patientLabel(report)}</small>}
        </span>
        {report.status !== 'done' && <span className="xo-lib-state"><StatusPill status={report.status} /></span>}
      </button>
    </li>
  );
}

function FolderRow({ folder, now, onOpen }) {
  return (
    <li>
      <button type="button" className="xo-lib-row is-folder" onClick={onOpen}>
        <span className="xo-lib-glyph is-small"><Folder aria-hidden="true" /></span>
        <span className="xo-lib-name">
          <strong>{folderName(folder)}</strong>
          {folder.kind === 'patient' && folder.subproject && <small>{folder.subproject.code || folder.subproject.name}</small>}
        </span>
        <span className="xo-lib-cell">
          {counted('ui.reports.inventory.count', folder.reports)}
          {folder.failed > 0 && <em className="xo-lib-flag">{text('failed_count', { count: NUMBER.format(folder.failed) })}</em>}
        </span>
        <span className="xo-lib-cell" title={dateLabel(folder.latestAt)}>{ago(folder.latestAt, now)}</span>
        <span className="xo-lib-cell is-size">{bytesLabel(folder.bytes)}</span>
        <ChevronRight className="xo-lib-row-end" aria-hidden="true" />
      </button>
    </li>
  );
}

function FileRow({ report, now, selected, withPatient, onSelect, onOpen }) {
  return (
    <li>
      <button type="button" className={`xo-lib-row is-file${selected ? ' is-selected' : ''}`} aria-pressed={selected}
        onClick={onSelect} onDoubleClick={onOpen} title={report.file === null ? undefined : text('open_hint')}>
        <span className={`xo-lib-doc is-${report.status}`} aria-hidden="true">PDF</span>
        <span className="xo-lib-name">
          <strong>{report.reportType.displayName}</strong>
          <small>{withPatient ? `${patientLabel(report)} · ${report.language.name}` : report.language.name}</small>
        </span>
        <span className="xo-lib-cell"><StatusPill status={report.status} /></span>
        <span className="xo-lib-cell" title={dateLabel(report.requestedAt)}>{ago(report.requestedAt, now)}</span>
        <span className="xo-lib-cell is-size">{report.file === null ? '' : bytesLabel(report.file.bytes)}</span>
        <span className="xo-lib-row-end" />
      </button>
    </li>
  );
}

function Details({ report, openError, onClose, onShowFolder }) {
  const facts = [
    [inventory('column_patient'), patientLabel(report)],
    [inventory('filter_project'), report.project === null ? text('no_project') : report.project.name],
  ];
  if (report.subproject !== null) facts.push([text('subproject'), report.subproject.name || report.subproject.code]);
  if (report.organization !== null) facts.push([inventory('organization'), report.organization.name]);
  facts.push(
    [inventory('company'), report.company.name],
    [inventory('filter_source'), <SourceMark source={report.source} />],
    [inventory('requested_by'), report.requestedBy === null ? sourceLabel(report.source) : (report.requestedBy.name || report.requestedBy.email)],
    [inventory('requested'), dateLabel(report.requestedAt)],
    [inventory('completed'), dateLabel(report.completedAt)],
    [text('generation'), durationLabel(report.generationMs)],
    [inventory('queue_wait'), durationLabel(report.waitMs)],
  );
  if (report.file !== null) facts.push([inventory('column_size'), bytesLabel(report.file.bytes)]);
  if (report.status === 'failed') facts.push([inventory('filter_reason'), reasonLabel(report.failureReason)]);
  if (report.pipeline !== null) facts.push([inventory('pipeline'), `${report.pipeline.displayName} ${report.pipeline.codeVersion}`]);
  if (report.product !== null) facts.push([inventory('product'), report.product.name]);
  if (report.collection !== null) {
    facts.push([inventory('collection'), <code>{report.collection.printedCode || report.collection.code || report.collection.uuid}</code>]);
    facts.push([inventory('validation'), report.collection.validation === null ? inventory('not_validated')
      : inventory(`validation_${report.collection.validation.status}`)]);
  }
  facts.push(
    [inventory('recipients'), report.recipients.total === 0 ? inventory('no_recipients') : inventory('recipients_value', {
      total: NUMBER.format(report.recipients.total), sent: NUMBER.format(report.recipients.sent), opened: NUMBER.format(report.recipients.opened),
    })],
    [inventory('report_id'), <code>{report.uuid}</code>],
  );
  if (report.file !== null && report.file.sha256) facts.push([inventory('checksum'), <code title={report.file.sha256}>{report.file.sha256.slice(0, 16)}</code>]);

  return (
    <aside className="xo-lib-details" aria-label={report.reportType.displayName}>
      <div className="xo-lib-details-preview">
        <Page status={report.status} large />
        <button type="button" className="xo-mob-icon-button xo-lib-close" onClick={onClose} aria-label={text('close')}><X aria-hidden="true" /></button>
      </div>
      <div className="xo-lib-details-title">
        <strong>{report.reportType.displayName}</strong>
        <small>{report.language.name} · {statusLabel(report.status)}</small>
      </div>
      <div className="xo-lib-details-actions">
        {report.file !== null && <DownloadReport uuid={report.uuid} />}
        {onShowFolder && (
          <button type="button" className="xo-btn-2" onClick={onShowFolder}><FolderOpen aria-hidden="true" /><span>{text('show_in_folder')}</span></button>
        )}
      </div>
      {openError && <small className="t-bad" role="alert">{openError}</small>}
      <dl>{facts.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
    </aside>
  );
}

// What an analytics link narrowed the list to, each removable.
function FilterChips({ filters, facets, onChange }) {
  const lists = facets ? {
    reportType: facets.reportTypes, language: facets.languages, pipeline: facets.pipelines, product: facets.products,
    organization: facets.organizations, company: facets.companies,
  } : {};
  const label = (name, value) => {
    if (name === 'status') return statusLabel(value);
    if (name === 'source') return sourceLabel(value);
    if (name === 'reason') return reasonLabel(value);
    const found = (lists[name] || []).find((row) => String(row.id ?? row.code) === value);
    if (!found) return value;
    return name === 'pipeline' ? `${found.name} ${found.codeVersion}` : found.name;
  };
  const used = FILTERS.filter((name) => filters[name]);
  return (
    <div className="xo-lib-chips">
      <span className="xo-rep-active">
        {used.map((name) => (
          <button key={name} type="button" onClick={() => onChange({ [name]: '' })}>
            <span>{label(name, filters[name])}</span><X aria-hidden="true" />
          </button>
        ))}
      </span>
      {used.length > 1 && (
        <button type="button" className="xo-link" onClick={() => onChange(Object.fromEntries(FILTERS.map((name) => [name, ''])))}>
          {inventory('clear_filters')}
        </button>
      )}
    </div>
  );
}

function emptyText(mode, list) {
  if (mode === 'list') return { title: text(`empty_${list}`) };
  if (mode === 'folder') return { title: text('empty_folder') };
  return { title: inventory('empty'), detail: inventory('empty_detail') };
}

export default function ReportLibrary() {
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(() => params.get('q') || '');
  const [selected, setSelected] = useState(null);
  const [openError, setOpenError] = useState(null);
  const [view, setView] = useState(storedView);
  const q = useDebounced(search.trim(), 280);
  const now = useNow(30000);
  const narrow = useNarrow();

  const project = params.get('project') || null;
  const patient = project === null ? null : params.get('patient') || null;
  const list = Object.hasOwn(LISTS, params.get('list') || '') ? params.get('list') : null;
  const urlQ = params.get('q') || '';
  const filters = useMemo(() => Object.fromEntries(FILTERS.map((name) => [name, params.get(name) || ''])), [params]);
  const filtered = FILTERS.some((name) => filters[name]);
  const mode = urlQ ? 'search' : filtered ? 'filtered' : list ? 'list' : 'folder';
  const sortKey = Object.hasOwn(SORTS, params.get('sort') || '') ? params.get('sort') : 'newest';
  const offset = Math.max(0, Number(params.get('offset')) || 0);

  // Opening a place is a step the browser's back button undoes; the sort stays.
  const go = useCallback((place) => {
    setSelected(null);
    setSearch('');
    setParams(() => {
      const next = new URLSearchParams();
      for (const [key, value] of Object.entries(place)) if (value !== null && value !== undefined && value !== '') next.set(key, String(value));
      if (sortKey !== 'newest') next.set('sort', sortKey);
      return next;
    });
  }, [setParams, sortKey]);

  const change = useCallback((updates) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      for (const [name, value] of Object.entries(updates)) {
        if (value === '' || value === null || value === undefined) next.delete(name); else next.set(name, String(value));
      }
      if (!Object.hasOwn(updates, 'offset')) next.delete('offset');
      return next;
    }, { replace: true });
  }, [setParams]);

  // The search box writes the address once typing settles, and the page reads the
  // address, so a copied link shows what is on screen and Back restores it.
  useEffect(() => {
    if (q !== search.trim() || q === urlQ) return;
    change({ q });
  }, [q, search, urlQ, change]);
  useEffect(() => { setSearch((current) => (current.trim() === urlQ ? current : urlQ)); }, [urlQ]);

  const loadRoot = useCallback(() => loadReportFolders(), []);
  const root = useLiveResource(['reports'], loadRoot, { reconcileMs: 120000 });
  const loadHere = useCallback(async () => ({ ...(await loadReportFolders({ project, patient })), at: `${project}/${patient}` }), [project, patient]);
  const here = useLiveResource(['reports'], loadHere, { reconcileMs: 60000, enabled: mode === 'folder' && project !== null });

  // The reports listed as files: a patient's, the loose ones without a patient,
  // a list from the sidebar, a search, or what an analytics link filtered.
  let where = null;
  if (mode === 'search') where = { q: urlQ };
  else if (mode === 'filtered') where = Object.fromEntries(FILTERS.filter((name) => filters[name]).map((name) => [name, filters[name]]));
  else if (mode === 'list') where = { list };
  else if (patient !== null) where = { project, patient };
  else if (project === 'none') where = { project: 'none', patient: 'none' };
  const filesKey = where === null ? null : JSON.stringify([where, sortKey, offset]);
  const loadFiles = useCallback(async () => {
    const [scope, sort, from] = JSON.parse(filesKey);
    const query = scope.list ? LISTS[scope.list].query() : scope;
    return { ...(await loadReportInventory({ ...query, ...SORTS[sort].files, limit: PAGE, offset: from })), key: filesKey };
  }, [filesKey]);
  const listed = useLiveResource(['reports'], loadFiles, { reconcileMs: 60000, enabled: filesKey !== null });
  const facets = useLiveResource(['reports'], loadReportFacets, { reconcileMs: 300000, enabled: mode === 'filtered' });

  const folder = mode !== 'folder' ? null : project === null ? root.data : (here.data && here.data.at === `${project}/${patient}` ? here.data : null);
  const folderState = project === null ? root : here;
  const fileData = listed.data && listed.data.key === filesKey ? listed.data : null;
  const folders = folder ? [...folder.folders].sort(SORTS[sortKey].folders) : [];
  const reports = fileData ? fileData.reports : [];
  const report = reports.find((row) => row.uuid === selected) || null;
  const withPatient = mode !== 'folder';

  const waitingFolder = mode === 'folder' && folder === null;
  const waitingFiles = filesKey !== null && fileData === null;
  const failure = (mode === 'folder' && folder === null && folderState.error) || (filesKey !== null && fileData === null && listed.error);
  const loading = !failure && (waitingFolder || waitingFiles);
  const empty = !loading && !failure && folders.length === 0 && reports.length === 0;

  function changeView(next) {
    setView(next);
    try { window.localStorage.setItem(VIEW_KEY, next); } catch (_error) { /* the choice holds for this visit */ }
  }

  function select(row) {
    setOpenError(null);
    setSelected(selected === row.uuid ? null : row.uuid);
  }

  function open(row) {
    if (row.file === null) return;
    setSelected(row.uuid);
    setOpenError(null);
    openReportFile(row.uuid).catch((caught) => setOpenError(errorMessage(caught)));
  }

  useEffect(() => {
    if (report === null) return undefined;
    const escape = (event) => { if (event.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [report]);

  // Breadcrumbs and the folder one level up.
  const home = { key: 'home', label: text('all_reports'), place: {} };
  let steps = [home];
  let up = null;
  if (mode === 'folder' && folder) {
    steps = [home, ...folder.path.map((step) => ({
      key: `${step.kind}-${step.id}`,
      label: folderName(step),
      place: step.kind === 'project' ? { project: step.id } : { project: folder.path[0].id, patient: step.id },
    }))];
    if (patient !== null) up = { project };
    else if (project !== null) up = {};
  } else if (mode === 'folder' && project !== null) {
    up = patient !== null ? { project } : {};
  } else if (mode === 'list') steps = [{ key: 'list', label: text(`list_${list}`) }];
  else if (mode === 'search') steps = [{ key: 'search', label: text('results', { term: urlQ }) }];
  else if (mode === 'filtered') steps = [home, { key: 'filtered', label: text('filtered') }];

  const tile = view === 'grid';
  const details = report && (
    <Details report={report} openError={openError} onClose={() => setSelected(null)}
      onShowFolder={mode === 'folder' ? null : () => go(folderOf(report))} />
  );
  return (
    <div className="xo-mobile-page xo-rep-page xo-lib">
      <Head title={files('title')} subtitle={text('subtitle')} />
      <div className={`xo-lib-frame${report && !narrow ? ' has-details' : ''}`}>
        <Sidebar root={root.data} mode={mode} list={list} project={project} onGo={go} />

        <section className="xo-lib-main">
          <div className="xo-lib-bar">
            <div className="xo-lib-where">
              {up !== null && (
                <button type="button" className="xo-mob-icon-button" onClick={() => go(up)} aria-label={files('up')} title={files('up')}>
                  <ChevronLeft aria-hidden="true" />
                </button>
              )}
              <Crumbs steps={steps} onGo={go} />
            </div>
            <div className="xo-lib-tools">
              <SearchField className="xo-lib-search" value={search} onChange={setSearch} placeholder={text('search')} autoComplete="off" />
              <FilterMenu icon={ArrowDownUp} label={files('sort')} value={sortKey} onChange={(value) => change({ sort: value === 'newest' ? '' : value })}
                options={Object.keys(SORTS).map((key) => ({ value: key, label: files(`sort_${key}`) }))} />
              <ViewSwitch value={view} onChange={changeView} label={text('layout')} />
            </div>
          </div>

          <div className="xo-lib-body">
            {mode === 'filtered' && <FilterChips filters={filters} facets={facets.data} onChange={change} />}
            {loading && <Loading label={files('loading')} />}
            {failure && <Failed message={failure} onRetry={() => { folderState.refresh(); listed.refresh(); }} />}
            {empty && <Empty {...emptyText(mode, list)} />}

            {folders.length > 0 && (
              <section className="xo-lib-group">
                <h3>{text('folders')}</h3>
                {tile ? (
                  <ul className="xo-lib-grid is-folders">
                    {folders.map((row) => <FolderTile key={`${row.kind}-${row.id}`} folder={row} now={now}
                      onOpen={() => go(row.kind === 'project' ? { project: row.id } : { project, patient: row.id })} />)}
                  </ul>
                ) : (
                  <ol className="xo-lib-rows">
                    {folders.map((row) => <FolderRow key={`${row.kind}-${row.id}`} folder={row} now={now}
                      onOpen={() => go(row.kind === 'project' ? { project: row.id } : { project, patient: row.id })} />)}
                  </ol>
                )}
              </section>
            )}

            {reports.length > 0 && (
              <section className="xo-lib-group">
                <h3>{mode === 'folder' ? text('files') : counted('ui.reports.inventory.count', fileData.total)}</h3>
                {tile ? (
                  <ul className="xo-lib-grid is-files">
                    {reports.map((row) => <FileTile key={row.uuid} report={row} now={now} selected={row.uuid === selected}
                      withPatient={withPatient} onSelect={() => select(row)} onOpen={() => open(row)} />)}
                  </ul>
                ) : (
                  <ol className="xo-lib-rows">
                    {reports.map((row) => <FileRow key={row.uuid} report={row} now={now} selected={row.uuid === selected}
                      withPatient={withPatient} onSelect={() => select(row)} onOpen={() => open(row)} />)}
                  </ol>
                )}
                {fileData.total > PAGE && (
                  <div className="xo-mob-pager">
                    <span>{inventory('showing', { from: NUMBER.format(offset + 1), to: NUMBER.format(offset + reports.length), total: NUMBER.format(fileData.total) })}</span>
                    <button type="button" className="xo-mob-icon-button" disabled={offset === 0}
                      onClick={() => change({ offset: Math.max(0, offset - PAGE) || '' })} aria-label={inventory('previous_page')}><ChevronLeft /></button>
                    <button type="button" className="xo-mob-icon-button" disabled={offset + PAGE >= fileData.total}
                      onClick={() => change({ offset: offset + PAGE })} aria-label={inventory('next_page')}><ChevronRight /></button>
                  </div>
                )}
              </section>
            )}
          </div>
        </section>

        {details && !narrow && details}
      </div>
      {details && narrow && createPortal(
        <div className="xo-mobile-page xo-rep-page xo-lib-overlay">
          <button type="button" className="xo-lib-scrim" aria-label={text('close')} onClick={() => setSelected(null)} />
          {details}
        </div>,
        document.getElementById('xo') ?? document.body,
      )}
    </div>
  );
}
